import axios from 'axios';
import config from '../config';

// Create axios instance with base URL from config
const api = axios.create({
  baseURL: config.apiUrl
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    // Attach auth only for admin/protected routes
    const isAdminRoute = typeof config.url === 'string' && (config.url.startsWith('/admin') || config.url.startsWith('/stock') || config.url.startsWith('/billing') || config.url.startsWith('/api-keys') || config.url.startsWith('/settlements') || config.url.startsWith('/users') || config.url.startsWith('/agents') || config.url.startsWith('/products') || config.url.startsWith('/categories') || config.url.startsWith('/orders') || config.url.startsWith('/push') || config.url.startsWith('/analytics') || config.url.startsWith('/procurement'));
    if (token && isAdminRoute) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Only handle auth for requests that used/need auth
      const hadAuthHeader = !!originalRequest.headers?.Authorization;
      const isAdminRoute = typeof originalRequest.url === 'string' && originalRequest.url.startsWith('/admin');
      if (!hadAuthHeader && !isAdminRoute) {
        // Public request failed with 401; don't clear tokens or redirect
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${config.apiUrl}/admin/refresh`, {
            refresh_token: refreshToken
          });

          const { access_token } = response.data;
          localStorage.setItem('accessToken', access_token);
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          
          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userInfo');
        
        // Only redirect if we're not already on login page
        if (window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// API functions for categories
export const getCategories = () => api.get('/categories');
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// API functions for products
export const getProducts = () => api.get('/products');
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// API functions for orders
export const getOrders = (username = null) => {
  const params = username ? { username } : {};
  return api.get('/orders', { params });
};
export const getOrdersSummary = (username = null) => {
  const params = username ? { username } : {};
  return api.get('/orders/summary', { params });
};
export const getRecentOrders = (limit = 20, username = null) => {
  const params = { limit };
  if (username) params.username = username;
  return api.get('/orders/recent', { params });
};
export const getProductStatusReport = () => api.get('/analytics/product-status-report');
export const getOrder = (orderId, { phone, signal } = {}) =>
  api.get(`/orders/${orderId}`, { params: phone ? { phone } : undefined, signal });
export const getOrderByToken = (token) => api.get(`/orders/confirmation/${token}`);
export const createOrder = (data) => api.post('/orders', data);
export const updateOrderStatus = (orderId, data) => {
  console.log('Sending status update:', data);
  return api.put(`/orders/${orderId}/status`, data);
};
export const updateOrder = (orderId, data) => api.put(`/orders/${orderId}`, data);
export const bulkUpdateOrderStatusByNumber = (data) => api.post('/orders/bulk-status-by-number', data);
export const getInvoice = (orderId, { phone } = {}) =>
  api.get(`/orders/${orderId}/invoice`, { params: phone ? { phone } : undefined, responseType: 'blob' });
export const getInvoiceByToken = (token) =>
  api.get(`/orders/confirmation/${token}/invoice`, { responseType: 'blob' });
export const deleteOrder = (orderId) => api.delete(`/orders/${orderId}`);
export const setOrderPackCount = (orderId, packCount) => api.put(`/stock/orders/${orderId}/pack-count`, { pack_count: packCount });
export const markOrderWhatsappContacted = (orderId) => api.put(`/orders/${orderId}/mark-whatsapp-contacted`);

// API functions for push notifications
export const getVapidPublicKey = () => api.get('/push/vapid-public-key');
export const subscribeToPush = (subscription) => api.post('/push/subscribe', subscription);
export const unsubscribeFromPush = (subscription) => api.post('/push/unsubscribe', subscription);

// API functions for authentication
export const login = (username, password) => api.post('/admin/login', { username, password });
export const refreshToken = (refreshToken) => api.post('/admin/refresh', { refresh_token: refreshToken });
export const getCurrentUser = () => api.get('/admin/me');

// API functions for user management
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const getAgents = () => api.get('/agents');

// API functions for billing
export const createBillingOrder = (data) => api.post('/billing/orders', data);

// API functions for bill drafts (park/resume in-progress bills)
export const listBillDrafts = (source) => api.get('/billing/drafts', { params: { source } });
export const createBillDraft = (data) => api.post('/billing/drafts', data);
export const updateBillDraft = (id, data) => api.put(`/billing/drafts/${id}`, data);
export const deleteBillDraft = (id) => api.delete(`/billing/drafts/${id}`);

// API functions for stock management
export const getGodowns = () => api.get('/stock/godowns');
export const createGodown = (data) => api.post('/stock/godowns', data);
export const getInventory = () => api.get('/stock/inventory');
export const addInventory = (data) => api.post('/stock/inventory', data);
export const getStockSummary = () => api.get('/stock/stock-summary');
export const getAllocations = (orderItemId) => api.get(`/stock/allocations/${orderItemId}`);
export const updatePackedQuantities = (orderId, data) => api.put(`/stock/pack/${orderId}`, data);
export const checkStockAvailability = (orderId) => api.get(`/stock/check-stock/${orderId}`);

// Reorder functions
export const reorderProducts = (items) => api.put('/products/reorder', items);
export const reorderCategories = (items) => api.put('/categories/reorder', items);

// Settlement API
export const settlementAPI = {
  getProfitReport: () => api.get('/admin/profit-report'),
  getUserSettlements: (userId) => api.get(`/settlements/${userId}`),
  createSettlement: (data) => api.post('/settlements', data)
};

// API Key management
export const apiKeyAPI = {
  listApiKeys: () => api.get('/api-keys'),
  getApiKey: (key) => api.get(`/api-keys/${key}`),
  getAvailablePermissions: () => api.get('/api-keys/permissions/available'),
  getApiKeyInfo: () => api.get('/api-keys/info')
};

// Procurement / Bulk Purchase Import (Diwali Story 1, Phase 1)
export const createProcurementImport = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/procurement/imports', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getProcurementImports = () => api.get('/procurement/imports');
export const getProcurementImport = (jobId) => api.get(`/procurement/imports/${jobId}`);
export const confirmProcurementColumnMap = (jobId, data) => api.put(`/procurement/imports/${jobId}/column-map`, data);
export const resolveProcurementImport = (jobId) => api.post(`/procurement/imports/${jobId}/resolve`);
export const bulkApproveProcurementExact = (jobId) => api.post(`/procurement/imports/${jobId}/bulk-approve-exact`);
export const updateProcurementRow = (jobId, rowId, data) => api.put(`/procurement/imports/${jobId}/rows/${rowId}`, data);
export const validateProcurementImport = (jobId) => api.post(`/procurement/imports/${jobId}/validate`);
export const postProcurementImport = (jobId, godownId) => api.post(`/procurement/imports/${jobId}/post`, null, { params: { godown_id: godownId } });
export const reverseProcurementPurchase = (headerId) => api.post(`/procurement/purchases/${headerId}/reverse`);
export const getProcurementSuppliers = () => api.get('/procurement/suppliers');
export const createProcurementSupplier = (data) => api.post('/procurement/suppliers', data);
export const getProcurementPurchases = (params) => api.get('/procurement/purchases', { params });
export const getProcurementPurchaseDetail = (headerId) => api.get(`/procurement/purchases/${headerId}`);
export const searchProcurementProducts = (q) => api.get('/procurement/products/search', { params: { q } });

// Analytics v2 / Expenses / Seasons (Diwali Phases 2-5)
export const getProfitability = () => api.get('/analytics/profitability');
export const getChannelSplit = () => api.get('/analytics/channel-split');
export const setOrderChannel = (orderId, channel) => api.put(`/analytics/orders/${orderId}/channel`, { channel });
export const getExecutiveDashboard = () => api.get('/analytics/dashboard');
export const backfillSaleCosts = () => api.post('/analytics/backfill-sale-costs');
export const getSeasons = () => api.get('/analytics/seasons');
export const createSeason = (data) => api.post('/analytics/seasons', data);
export const closeSeason = (seasonId) => api.post(`/analytics/seasons/${seasonId}/close`);
export const getExpenseCategories = () => api.get('/expenses/categories');
export const createExpenseCategory = (data) => api.post('/expenses/categories', data);
export const getExpenses = (params) => api.get('/expenses', { params });
export const createExpense = (data) => api.post('/expenses', data);
export const addExpensePayment = (id, data) => api.post(`/expenses/${id}/payments`, data);
export const reverseExpense = (id) => api.post(`/expenses/${id}/reverse`);
export const getPnL = () => api.get('/expenses/pnl');

export default api;